import { Box, List, ListItemText, Typography } from '@material-ui/core';
import {
  EntityInfoCard,
  useEntity,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { Text } from '@backstage/ui';

enum GroupAnnotationType {
  ENTITY_REF,
  DATE,
}

type GroupAnnotationItem = {
  label: string;
  annotationKey: string;
  type: GroupAnnotationType;
};

const GROUP_ANNOTATIONS: GroupAnnotationItem[] = [
  {
    label: 'Created by',
    annotationKey: 'devportal.traton.com/group.created-by',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Created at',
    annotationKey: 'devportal.traton.com/group.created-at',
    type: GroupAnnotationType.DATE,
  },
  {
    label: 'Updated by',
    annotationKey: 'devportal.traton.com/group.updated-by',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Updated at',
    annotationKey: 'devportal.traton.com/group.updated-at',
    type: GroupAnnotationType.DATE,
  },
  {
    label: 'Contact Person',
    annotationKey: 'devportal.traton.com/group.contact-person',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Solution Architect',
    annotationKey: 'devportal.traton.com/group.solution-architect',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Release Train Engineer',
    annotationKey: 'devportal.traton.com/group.release-train-engineer',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Flow Accountable (VCT)',
    annotationKey: 'devportal.traton.com/group.flow-accountable-vct',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'System Architect',
    annotationKey: 'devportal.traton.com/group.system-architect',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Product Manager',
    annotationKey: 'devportal.traton.com/group.product-manager',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Solution Manager',
    annotationKey: 'devportal.traton.com/group.solution-manager',
    type: GroupAnnotationType.ENTITY_REF,
  },
  {
    label: 'Solution Train Engineer',
    annotationKey: 'devportal.traton.com/group.solution-train-engineer',
    type: GroupAnnotationType.ENTITY_REF,
  },
];

function renderEntityRefLinks(entityRefs?: string) {
  const parsedEntityRefs = entityRefs
    ?.split(',')
    .map(entityRef => entityRef.trim())
    .filter(Boolean);

  if (!parsedEntityRefs?.length) {
    return <Typography color="textSecondary">N/A</Typography>;
  }

  return (
    <List>
      {parsedEntityRefs.map(entityRef => (
        <ListItemText>
          <EntityRefLink key={entityRef} entityRef={entityRef} />
        </ListItemText>
      ))}
    </List>
  );
}

function renderGroupAnnotations(
  annotations: Record<string, string> | undefined,
) {
  return GROUP_ANNOTATIONS.filter(({ annotationKey }) =>
    Boolean(annotations?.[annotationKey]),
  ).map(({ label, annotationKey, type }) =>
    type === GroupAnnotationType.ENTITY_REF ? (
      <Box key={annotationKey} paddingBottom={1}>
        <Typography variant="button" color="textSecondary">
          {label}
        </Typography>
        <Box>{renderEntityRefLinks(annotations?.[annotationKey])}</Box>
      </Box>
    ) : (
      <Box key={annotationKey}>
        <Typography variant="button" color="textSecondary">
          {label}
        </Typography>
        <Box paddingBottom={1} paddingTop={0.5}>
          <Typography color="textSecondary">
            {new Date(annotations?.[annotationKey]!).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>
    ),
  );
}

export function GroupInfoCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations;

  return (
    <EntityInfoCard title={<Box pt={1.1}>Group Information</Box>}>
      <Box pt={1.3} pb={3}>
        <Text color="secondary">Details about the group and its members</Text>
      </Box>
      <Box
        pt={1}
        paddingX={1}
        display="flex"
        flexDirection="column"
        style={{ gap: '16px' }}
      >
        {renderGroupAnnotations(annotations)}
      </Box>
    </EntityInfoCard>
  );
}
